import { Checkbox } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { MotherOrderList_orders_edges_node } from "@saleor/motherOrders/types/MotherOrderList";
import RemittanceRunnerCheckbox from "@saleor/remittanceRunner/components/RemittanceRunnerCheckbox";
import { RemittanceMotherOrder } from "@saleor/remittanceRunner/utils/formDataTypes";
import StoreOrderStatus from "@saleor/storeOrders/components/StoreOrderStatus/StoreOrderStatus";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    colCheckbox: {
      width: "65px !important"
    },
    colRemittanceIdNo: {
      fontSize: "16px",
      maxWidth: "300px",
      whiteSpace: "normal",
      width: "220px",
      wordBreak: "break-word",
      [theme.breakpoints.down("md")]: {
        fontSize: "14px",
        maxWidth: "180px",
        width: "180px"
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "14px",
        maxWidth: "140px",
        width: "140px"
      }
    },
    remittanceCheckbox: {
      "&.Mui-disabled": {
        "& .MuiSvgIcon-root": {
          "& .innerBox": { fill: "#3D3D3D80" },
          "& path": { fill: "transparent" }
        },
        color: "#3D3D3D80"
      },
      color: "#1848FE"
    },
    table: {
      tableLayout: "fixed"
    },
    tableCellHeader: {
      fontSize: "14px",
      fontWeight: 400,
      [theme.breakpoints.down("md")]: {
        fontSize: "1em"
      }
    }
  }),
  { name: "PandaGoRemittanceList" }
);

interface PandaGoRemittanceListProp extends ListProps {
  remittance: MotherOrderList_orders_edges_node[];
  userCanOverrideRemittance: boolean;
  remittanceIdList: string[] | null;
  setMotherOrderList: React.Dispatch<React.SetStateAction<string[]>>;
  setMotherOrderCode: React.Dispatch<
    React.SetStateAction<RemittanceMotherOrder>
  >;
}

const PandaGoRemittanceList: React.FC<PandaGoRemittanceListProp> = props => {
  const {
    settings,
    disabled,
    remittance,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    userCanOverrideRemittance,
    remittanceIdList = [],
    setMotherOrderList,
    setMotherOrderCode
  } = props;

  const colNum = userCanOverrideRemittance ? 7 : 6;
  const classes = useStyles(props);

  const decimalFormatter = new Intl.NumberFormat("en-PH", {
    currency: "PHP",
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    style: "currency"
  });

  const allIds = remittance?.map(r => r.id) ?? [];
  const isAllSelected = allIds.every(id => remittanceIdList.includes(id));
  const isPartialSelected = allIds.some(id => remittanceIdList.includes(id));

  const hasSelectableRemittance = remittance?.some(
    r => !r?.remittanceLine?.remittance?.remittanceNo
  );

  const handleSelectAll = () => {
    const selectableRemittances = remittance.filter(
      r => !r?.remittanceLine?.remittance?.remittanceNo
    );
    const selectableIds = selectableRemittances.map(r => r.id);

    const isAllSelectableSelected = selectableIds.every(id =>
      remittanceIdList.includes(id)
    );

    if (isAllSelectableSelected) {
      setMotherOrderList([]);
      setMotherOrderCode(prev => ({
        ...prev,
        courier: "",
        orderCode: ""
      }));
    } else {
      setMotherOrderList(selectableIds);
      if (selectableRemittances.length > 0) {
        setMotherOrderCode(prev => ({
          ...prev,
          courier: selectableRemittances[0]?.courier,
          orderCode: selectableRemittances[0]?.orderCode
        }));
      }
    }
  };

  const handleSelectOne = (id: string) => {
    const isAlreadySelected = remittanceIdList.includes(id);
    const newList = isAlreadySelected
      ? remittanceIdList.filter(item => item !== id)
      : [...remittanceIdList, id];

    setMotherOrderList(newList);

    if (!isAlreadySelected) {
      const selectedItem = remittance.find(r => r.id === id);
      if (selectedItem?.orderCode) {
        setMotherOrderCode(prev => ({
          ...prev,
          courier: selectedItem?.courier,
          orderCode: selectedItem?.orderCode
        }));
      }
    }
  };

  return (
    <ResponsiveTable className={classes.table}>
      <TableHead>
        {userCanOverrideRemittance && (
          <TableCellHeader className={classes.colCheckbox}>
            <Checkbox
              className={classes.remittanceCheckbox}
              icon={<RemittanceRunnerCheckbox />}
              checked={isAllSelected}
              indeterminate={!isAllSelected && isPartialSelected}
              onChange={handleSelectAll}
              disabled={!hasSelectableRemittance}
            />
          </TableCellHeader>
        )}
        <TableCellHeader className={classes.tableCellHeader}>
          <FormattedMessage defaultMessage="Mother Order Code" />
        </TableCellHeader>
        <TableCellHeader className={classes.tableCellHeader}>
          <FormattedMessage defaultMessage="Status" />
        </TableCellHeader>
        <TableCellHeader className={classes.tableCellHeader}>
          <FormattedMessage defaultMessage="Remittance ID" />
        </TableCellHeader>
        <TableCellHeader className={classes.tableCellHeader}>
          <FormattedMessage defaultMessage="Rider Name" />
        </TableCellHeader>
        <TableCellHeader className={classes.tableCellHeader}>
          <FormattedMessage defaultMessage="Mode of Payment" />
        </TableCellHeader>
        <TableCellHeader className={classes.tableCellHeader}>
          <FormattedMessage defaultMessage="Total Amount Today" />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={colNum}
            settings={settings}
            hasNextPage={
              pageInfo && !disabled ? pageInfo.hasNextPage : undefined
            }
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {remittance &&
          renderCollection(
            remittance,
            (remittance, index) => {
              const isChecked = remittanceIdList.includes(remittance.id);
              return (
                <TableRow
                  hover={!!remittance}
                  key={remittance ? index : "skeleton"}
                >
                  {userCanOverrideRemittance && (
                    <TableCell className={classes.colCheckbox}>
                      <Checkbox
                        className={classes.remittanceCheckbox}
                        icon={<RemittanceRunnerCheckbox />}
                        checked={isChecked}
                        onChange={() => handleSelectOne(remittance.id)}
                        disabled={
                          !userCanOverrideRemittance ||
                          !!remittance?.remittanceLine?.remittance?.remittanceNo
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => remittance.orderCode,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    <StoreOrderStatus status={remittance.smopStatus} />
                  </TableCell>
                  <TableCell className={classes.colRemittanceIdNo}>
                    {maybe<React.ReactNode>(
                      () =>
                        remittance.remittanceLine.remittance.remittanceNo ||
                        "---",
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () =>
                        remittance.rider === null
                          ? remittance.thirdPartyRider.firstName
                          : remittance.rider.firstName,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {remittance?.payments?.length
                      ? remittance.payments
                          .map(
                            type =>
                              type.paymentMethodType.charAt(0).toUpperCase() +
                              type.paymentMethodType.slice(1)
                          )
                          .join(", ")
                      : "---"}
                  </TableCell>
                  <TableCell>
                    {maybe(() => remittance.total) ? (
                      decimalFormatter.format(remittance.total.gross.amount)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              );
            },
            () => (
              <TableRow>
                <TableCell colSpan={colNum}>
                  <FormattedMessage defaultMessage="No remittances found" />
                </TableCell>
              </TableRow>
            )
          )}
      </TableBody>
    </ResponsiveTable>
  );
};

export default PandaGoRemittanceList;
